// This is to simulate data coming from drupal API.
import { validatePackageData } from './validator';
import { packageRequest, packageReceiveAction } from '../../actions/Section';
import { appErrorAction } from '../../actions/App';
import { callApiGet } from './common';
import { PACKAGE_ENDPOINT } from '../../constants/Api';
import { getAllQueryParameters, mergeRecursive, getAge, clone } from '../common';
import { userDataService } from './userData';
import { userDataTokenUpdateAction } from '../../actions/UserData';
import { getDateOfBirth } from '../form/formValidators';

import env from '../../env.json';

/**
 * Determines if the state allows us to fetch data.
 * @param  {object} state The current state.
 * @return {boolean}      If we should fetch or not.
 */
const shouldFetch = (state) => {
  if (state.section) {
    const data = state.section.packageData;
    if (Object.keys(data).length === 0) {
      return true;
    }
    if (state.isFetching) {
      return false;
    }
  }

  return true;
};

/**
 * Get merge package including static, business and global.
 * @return {Object}
 */
export const mergePackArray = async (pack, arrayPack) => {
  if (!arrayPack[0]) {
    // eslint-disable-next-line no-console
    console.log('error missing root package', arrayPack);
    return {};
  }
  const newPack = clone(await arrayPack[0]);
  let postOverride; // PostMergeOverride is used to overwrite static (drupal).
  if (newPack.postOverride) {
    ({ postOverride } = newPack);
    delete newPack.postOverride;
  }
  let packResult = mergeRecursive(pack, newPack);
  if (arrayPack[1]) {
    packResult = mergeRecursive(packResult, await arrayPack[1]);
  }
  if (arrayPack[2]) {
    packResult = mergeRecursive(packResult, await arrayPack[2]);
  }
  if (postOverride) {
    packResult = mergeRecursive(packResult, postOverride);
  }

  return packResult;
};

/**
 * Return the merge local package.
 * @param pack (object)
 * @param packData (object)
 * @param localEnv (object)
 * @param businessUnit (object)
 * @param hasBUTranslations
 * @returns {Promise<{}>}
 */
const getPackageMergeBUFlow = async (pack, packData, localEnv, businessUnit, hasBUTranslations) => {
  let localPack = pack;
  // Business Unit product specific flow override of package.
  const businessUnitFlow = packData[localEnv.businessUnit];
  if (businessUnitFlow) {
    const hasBusinessUnitVariant =
        localEnv.variant && businessUnitFlow.variant && businessUnitFlow.variant[localEnv.variant];
    if (businessUnitFlow[localEnv.package]) {
      // Override Business Unit specific translations for base product when there is no variant.
      if (!hasBusinessUnitVariant && hasBUTranslations) {
        localPack = mergeRecursive(localPack, businessUnit[1]);
      }
      // IMPORTANT: Has to have await before mergePackArray else the merge result will a promise.
      localPack = await mergePackArray(localPack, businessUnitFlow[localEnv.package]);
    }
    // The business unit variant of the product specific flow.
    if (hasBusinessUnitVariant) {
      // Override Business Unit specific translations for base business unit flow.
      if (hasBUTranslations) {
        localPack = await mergeRecursive(localPack, businessUnit[1]);
      }
      // IMPORTANT: Has to have await before mergePackArray else the merge result will a promise.
      localPack = mergePackArray(localPack, businessUnitFlow.variant[localEnv.variant]);
    }
  }
  return localPack;
};

/**
 * Get Package Merge in the order like in drupal with inheritance.
 * @param localPackData (object)
 * @param localEnv (object)
 * @returns {Promise<{}>}
 */
export const getPackageMerge = async (localPackData, localEnv) => {
  // Global package.
  let pack = mergeRecursive(await localPackData.global[0], await localPackData.global[1]);
  // Business Unit specific flow override of global.
  const businessUnit = localPackData.businessUnit[localEnv.businessUnit];
  if (businessUnit) {
    // IMPORTANT: Has to have await before mergePackArray else the merge result will a promise.
    pack = await mergePackArray(pack, businessUnit);
  }
  // The product specific flows of the package.
  // IMPORTANT: Has to have await before mergePackArray else the merge result will a promise.
  pack = await mergePackArray(pack, localPackData[localEnv.package]);
  // Local json (node) either variant or business unit overrides.
  const hasBUTranslations = businessUnit && businessUnit[1];
  // The variant of the product flow.
  if (localEnv.variant && localPackData.variant && localPackData.variant[localEnv.variant]) {
    // Override Business Unit specific translations for base product.
    if (hasBUTranslations) {
      pack = mergeRecursive(pack, businessUnit[1]);
    }
    // IMPORTANT: Has to have await before mergePackArray else the merge result will a promise.
    pack = await mergePackArray(pack, localPackData.variant[localEnv.variant]);
  }
  return getPackageMergeBUFlow(pack, localPackData, localEnv, businessUnit, hasBUTranslations);
};

export const getPackage = async () => {
  const { sharedPackage } = await import('./package-shared.js');
  const { localPackage } = await import('./package-local.js');
  const packageData = {
    ...sharedPackage,
    ...localPackage,
  };
  return getPackageMerge(packageData, env);
};

/**
 * Fetches the hardcoded json file.
 * @return {Promise} A Promise that will resolve/reject after 1500ms.
 */
const getPackageFromLocal = () => {
  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      const pack = await getPackage();
      const validatorResult = validatePackageData(pack);
      if (validatorResult.errors.length === 0) {
        resolve(pack);
      } else {
        // Throw validation error.
        const errorData = {
          errorType: 'validationError',
          errorData: validatorResult.errors,
        };
        reject(errorData);
      }
    }, 1500);
  });
};

/**
 *  Getting flow data from server.
 * @param dispatch
 * @param state
 * @returns {Promise<*>}
 */
export const getPackageFromDrupal = (dispatch, state = '') => {
  const params = {
    path: state.app.pathname || window.location.pathname,
  };
  return callApiGet(PACKAGE_ENDPOINT, params, dispatch, false, false)
    .then(data => dispatch(packageReceiveAction(data)))
    .catch(data => dispatch(appErrorAction(data)));
};

function loadPackage(dispatch, mock, state) {
  if (import.meta.env.DEV) {
    return getPackageFromLocal()
      .then(data => dispatch(packageReceiveAction(data, mock)))
      .catch(data => dispatch(appErrorAction(data)));
  }
  // Call service for fetching the package.
  return getPackageFromDrupal(dispatch, state);
}

/**
 * Fetches package data and validates it.
 * @return {function} Dispatch function.
 */
const fetchPackageData = (state = { app: {} }, mock = false) => {
  return dispatch => {
    dispatch(packageRequest());
    // If we are on dev we use the hardcoded json.
    const allQueryParams = getAllQueryParameters();
    if (allQueryParams.state) {
      const stateToken = allQueryParams.state;
      return dispatch(userDataService.getStateData(stateToken))
        .then((userStatus) => {
          if (userStatus.id_token && userStatus.access_token) {
            const userStatusNew = userStatus;
            userStatusNew.loginStatus = true;
            userStatusNew.loginErrorCode = null;
            let val = userStatusNew.civilRegistrationNumber;
            if (val && val.length === 10) {
              val = `${val.substring(0, 6)}-${val.substring(6, 10)}`;
              const dob = getDateOfBirth(val);
              userStatusNew.civilRegistrationNumber = val;
              userStatusNew.age = getAge(dob);
            }
            // To skip login page on realod
            sessionStorage.setItem('action', 'next');
            // Updating Userdata from login response
            dispatch(userDataTokenUpdateAction(userStatusNew));
          } else {
            const userStatusNew = {};
            userStatusNew.loginStatus = false;
            userStatusNew.loginErrorCode = allQueryParams.err_code;
            dispatch(userDataTokenUpdateAction(userStatusNew));
          }
          // Loading the flow
          return loadPackage(dispatch, mock, state);
        });
    }
    return loadPackage(dispatch, mock, state);
  };
};

/**
 * Fetches a package from API.
 * @return {object} The Promise to fetch data.
 */
export const getPackageData = (mock = false) => {
  return (dispatch, getState) => {
    const state = getState();
    if (shouldFetch(state)) {
      // Dispatch a thunk from thunk!
      return dispatch(fetchPackageData(state, mock));
    }
    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};
